import React, { useEffect, useMemo, useState } from 'react';
import { Chessboard } from 'react-chessboard';
import { Trophy, Timer, X, CheckCircle2, Loader2, Clock, XCircle, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { toast } from 'sonner';

/**
 * Wide "Live View" monitor for NORMAL puzzles (non-Capture-Activity) rendered
 * in the MAIN classroom area while the teacher has launched an ASK.
 *
 * Data source (all pre-existing, no new endpoints):
 *   · room.active_puzzle.{title, started_at, duration_seconds, max_attempts, fen}
 *   · room.puzzle_states[uid] populated by server.py /puzzle/attempt on every
 *     student move — { fen, last_move, attempts_used, attempts_left, status,
 *     solved, sol_index, updated_at }
 *   · room.puzzle_solves[] populated on solve — { user_id, correct,
 *     elapsed_ms, points_awarded }
 *   · room.participants (filtered client-side to hide teacher)
 *
 * Parallel to TeacherCaptureLiveView.jsx — same shell, header, countdown,
 * grid pattern. Kept as a SEPARATE file so Capture stays untouched.
 */

const STATUS = {
  waiting:   { label: 'Sin iniciar', pill: 'bg-slate-400',   ring: 'border-slate-200 bg-white',           icon: Clock,        accent: 'text-slate-500'   },
  solving:   { label: 'Resolviendo', pill: 'bg-amber-500',   ring: 'border-amber-300 bg-amber-50/60',     icon: Loader2,      accent: 'text-amber-700'   },
  incorrect: { label: 'Falló',       pill: 'bg-rose-500',    ring: 'border-rose-300 bg-rose-50/60',       icon: XCircle,      accent: 'text-rose-700'    },
  locked:    { label: 'Bloqueado',   pill: 'bg-slate-700',   ring: 'border-slate-300 bg-slate-100',       icon: Lock,         accent: 'text-slate-800'   },
  solved:    { label: 'Resuelto',    pill: 'bg-emerald-500', ring: 'border-emerald-300 bg-emerald-50/60', icon: CheckCircle2, accent: 'text-emerald-700' },
};

const DEFAULT_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

function StudentMiniBoard({ fen }) {
  // Puzzle normal path: the server already writes the full FEN into
  // puzzle_states[uid].fen (server.py:1441, 1477) after every attempt — no
  // need for piecesToFen. Fallback to the puzzle's initial FEN via prop.
  return (
    <div className="w-full aspect-square rounded-lg overflow-hidden pointer-events-none select-none" data-testid="puzzle-student-live-board">
      <Chessboard
        key={fen || DEFAULT_FEN}
        options={{
        position: fen || DEFAULT_FEN,
        boardOrientation: 'white',
        allowDragging: false,
        allowDrawingArrows: false,
        darkSquareStyle:  { backgroundColor: '#B58863' },
        lightSquareStyle: { backgroundColor: '#F0D9B5' },
        showAnimations: true,
        animationDurationInMs: 150,
      }}/>
    </div>
  );
}

export default function TeacherPuzzleLiveView({ room }) {
  const p = room?.active_puzzle;
  const states = room?.puzzle_states || {};
  const solves = room?.puzzle_solves || [];
  const participants = (room?.participants || []).filter(x => x.role !== 'teacher');

  // Countdown — same math TeacherCaptureLiveView / LivePuzzle uses. Single
  // source of truth: active_puzzle.started_at + duration_seconds.
  const [remaining, setRemaining] = useState(p?.duration_seconds || 0);
  const [expired, setExpired] = useState(false);
  const [nowTs, setNowTs] = useState(Date.now());
  useEffect(() => {
    if (!p) return;
    const started = new Date(p.started_at).getTime();
    const dur = (p.duration_seconds || 60) * 1000;
    const tick = () => {
      const now = Date.now();
      setNowTs(now);
      const r = Math.max(0, Math.round((started + dur - now) / 1000));
      setRemaining(r);
      setExpired(r <= 0);
    };
    tick();
    const iv = setInterval(tick, 500);
    return () => clearInterval(iv);
  }, [p?.id, p?.started_at, p?.duration_seconds]);

  const maxAttempts = p?.max_attempts || 3;

  // Map every non-teacher participant to a card. Absence from puzzle_states
  // means the student hasn't made their first attempt yet → "Sin iniciar".
  const cards = useMemo(() => participants.map(pt => {
    const st = states[pt.user_id];
    const solveRow = solves.find(s => s.user_id === pt.user_id && s.correct);
    // Prefer per-student stored elapsed on solve; otherwise derive from the
    // puzzle's global started_at (all students see the same clock).
    let elapsedMs;
    if (solveRow && typeof solveRow.elapsed_ms === 'number') {
      elapsedMs = solveRow.elapsed_ms;
    } else if (p?.started_at) {
      elapsedMs = Math.max(0, nowTs - new Date(p.started_at).getTime());
    } else {
      elapsedMs = 0;
    }
    const status = st?.kind === 'play_computer'
      ? (st?.completed
          ? 'solved'
          : st?.locked
            ? 'locked'
            : st?.attempts_used > 0
              ? 'solving'
              : 'waiting')
      : (st?.status || 'waiting');

    return {
      userId: pt.user_id,
      name: pt.name,
      status,
      attemptsUsed: st?.attempts_used ?? 0,
      attemptsLeft: st?.attempts_left ?? maxAttempts,
      solved: !!st?.solved || !!st?.completed,
      lastMove: st?.last_move || null,
      fen: st?.fen || p?.fen || DEFAULT_FEN,
      elapsedMs,
      points: st?.points_earned ?? solveRow?.points_awarded ?? null,
    };
  }), [participants, states, solves, maxAttempts, p?.fen, p?.started_at, nowTs]);

  const solvedCount = cards.filter(c => c.status === 'solved').length;
  const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
  const ss = String(remaining % 60).padStart(2, '0');
  const cd = p?.duration_seconds || 0;
  const cdPct = cd > 0 ? Math.max(0, Math.min(100, (remaining / cd) * 100)) : 0;
  const cdBar = expired ? 'bg-rose-500' : remaining <= 10 ? 'bg-rose-500' : remaining <= 30 ? 'bg-amber-500' : 'bg-emerald-500';

  const endActivity = async () => {
    try { await api.post(`/classrooms/${room.id}/puzzle/end`); }
    catch { toast.error('No se pudo cerrar el ejercicio'); }
  };

  const fmtTime = (ms) => {
    const total = Math.floor(ms / 1000);
    const m = String(Math.floor(total / 60)).padStart(2, '0');
    const s = String(total % 60).padStart(2, '0');
    return `${m}:${s}`;
  };

  if (!p || p.capture_config) return null;

  return (
    <div data-testid="teacher-puzzle-live-view" className="w-full h-full flex flex-col min-h-0">
      {/* Header — title, countdown, Cerrar ejercicio */}
      <div className="rounded-2xl border-2 border-indigo-400 bg-gradient-to-br from-indigo-50 via-white to-indigo-50/40 p-5 mb-4 shrink-0">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 rounded-2xl bg-indigo-500 text-white grid place-items-center shrink-0 shadow-md">
              <Trophy className="w-6 h-6"/>
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-bold uppercase tracking-widest text-indigo-700">Ejercicio en vivo</div>
              <div className="text-2xl font-display font-extrabold text-slate-900 leading-tight truncate" data-testid="puzzle-live-view-title">{p.title}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {solvedCount} de {cards.length} alumno{cards.length !== 1 ? 's' : ''} resolvió · {cards.length} conectado{cards.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
          <Button
            data-testid="end-puzzle-live-btn"
            size="lg"
            className="bg-rose-500 hover:bg-rose-600 text-white font-bold shrink-0 rounded-full px-6"
            onClick={endActivity}
          >
            <X className="w-5 h-5 mr-2"/> Terminar ejercicio
          </Button>
        </div>

        {/* Countdown */}
        <div className="mt-4" data-testid="puzzle-countdown">
          <div className="flex items-center justify-between mb-2">
            <span className={`inline-flex items-center gap-2 font-mono ${expired ? 'text-rose-600 font-extrabold text-base' : 'text-slate-700 font-bold text-lg'}`}>
              <Timer className={`w-5 h-5 ${expired ? 'text-rose-500' : 'text-slate-500'}`}/>
              {expired ? 'Tiempo agotado' : `Tiempo restante: ${mm}:${ss}`}
            </span>
            <span className="text-xs font-mono text-slate-500">{cd}s totales</span>
          </div>
          <div className="h-2.5 bg-white rounded-full overflow-hidden border border-slate-200">
            <div className={`h-full rounded-full transition-all duration-500 ${cdBar}`} style={{width: `${cdPct}%`}}/>
          </div>
        </div>
      </div>

      {/* Student cards grid */}
      <div className="flex-1 min-h-0 overflow-y-auto pr-1">
        {cards.length === 0 ? (
          <div className="h-full grid place-items-center text-slate-400 text-sm border border-dashed border-slate-200 rounded-2xl">
            Sin alumnos conectados
          </div>
        ) : (
          <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
            {cards.map(card => {
              const s = STATUS[card.status] || STATUS.waiting;
              const Icon = s.icon;
              const spin = card.status === 'solving' ? 'animate-spin' : '';
              return (
                <div key={card.userId}
                  data-testid={`puzzle-live-student-card-${card.userId}`}
                  className={`rounded-2xl border-2 ${s.ring} p-4 shadow-sm hover:shadow-md transition-shadow`}>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="min-w-0">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Alumno</div>
                      <div className="text-lg font-display font-bold text-slate-900 truncate" title={card.name}>{card.name}</div>
                    </div>
                    <span className={`inline-flex items-center gap-1 rounded-full ${s.pill} text-white text-[11px] font-bold px-3 py-1 uppercase tracking-wider shrink-0`}>
                      <Icon className={`w-3.5 h-3.5 ${spin}`}/> {s.label}
                    </span>
                  </div>
                  {/* Read-only mini board — mirrors the student's live FEN */}
                  <div className="mb-3">
                    <StudentMiniBoard fen={card.fen}/>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div className="rounded-xl bg-white border border-slate-200 p-3">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Intentos</div>
                      <div className={`text-2xl font-extrabold ${s.accent}`}>
                        {card.attemptsUsed} <span className="text-slate-400 text-lg">/ {maxAttempts}</span>
                      </div>
                    </div>
                    <div className="rounded-xl bg-white border border-slate-200 p-3">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Tiempo</div>
                      <div className={`text-2xl font-extrabold ${s.accent}`}>{fmtTime(card.elapsedMs)}</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-slate-500 font-mono">
                    <span>Último: <span className="font-bold text-slate-700">{card.lastMove || '—'}</span></span>
                    {card.points !== null && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 font-bold" data-testid={`puzzle-live-points-${card.userId}`}>
                        +{card.points} pts
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
